<template>
  <form @submit.prevent="saveForm()">
    <div class="modal-card" style="width: auto">
      <header class="modal-card-head flex justify-between">
        <p class="modal-card-title">
          {{ question ? 'Edit' : 'Add' }} Question
        </p>
      </header>
      <section class="modal-card-body space-y-6 max-h-[70vh] overflow-y-auto px-4">
        <d-field label="Question Text">
          <d-input v-model="form.content" />
        </d-field>

        <d-field label="Question Type" class="w-full">
            <d-select v-model="form.type" required>
                <option value="Multiple Choice">
                    Multiple Choice
                </option>
                <option value="Text">
                    Text Response
                </option>
                <option value="Drop Down">
                    Drop Down
                </option>
                <option value="Multiple Answer">
                    Multiple Answer
                </option>
                <option value="File Upload">
                    File Upload
                </option>
            </d-select>
        </d-field>

        <!-- Question Type Instructions -->
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p v-if="form.type =='Multiple Answer'" class="text-sm text-blue-800">
                Add as many choices as desired, and select multiple correct answer choices. De-select a choice to remove it.
            </p>
            <p v-if="form.type =='Drop Down' || form.type =='Multiple Choice'" class="text-sm text-blue-800">
                Add as many choices as desired, and select one correct answer choice
            </p>
            <p v-if="form.type =='File Upload'" class="text-sm text-blue-800">
                 Users will be able to upload files as their answer
            </p>
            <p v-if="form.type =='Text'" class="text-sm text-blue-800">
                 Users will be able to supply free text as their answer
            </p>
        </div>
        <!-- Answer Choices Section -->
        <div v-if="form.type == 'Multiple Choice' || form.type == 'Drop Down' || form.type == 'Multiple Answer'" class="space-y-4">
          <h4 class="text-lg font-semibold text-gray-800">Answer Choices</h4>
          
          <div v-for="(choice, index) in form.answerChoices" :key="choice.id" class="border rounded-lg p-4 transition-all duration-200" :class="{'bg-green-50 border-green-300': choice.isCorrect, 'bg-gray-50 border-gray-200': !choice.isCorrect}">
            <div class="flex items-center justify-between mb-3">
              <label class="text-sm font-semibold text-gray-700">
                Choice #{{ index + 1 }}
              </label>
              <div class="flex items-center space-x-3">
                <button
                  type="button"
                  @click="markAsCorrect(index)"
                  class="px-3 py-1 text-xs font-medium rounded-md transition-colors duration-200"
                  :class="choice.isCorrect ? 'bg-green-600 text-white' : 'bg-green-100 text-green-700 hover:bg-green-200'"
                >
                  {{ choice.isCorrect ? 'Correct Answer' : 'Mark as Correct' }}
                </button>
                <button
                  type="button"
                  @click="form.answerChoices.splice(index, 1)"
                  class="px-3 py-1 text-xs font-medium bg-red-100 text-red-700 hover:bg-red-200 rounded-md transition-colors duration-200"
                >
                  Remove
                </button>
              </div>
            </div>
            <d-input v-model="choice.content" placeholder="Enter choice text..." />
          </div>

          <div class="pt-2">
            <d-btn size="sm" color="secondary" @click="addChoice()">
              <d-icon icon="plus" class="mr-1" />
              Add Choice
            </d-btn>
          </div>
        </div>
      </section>
      <footer class="modal-card-foot justify-end space-x-3 pt-6 border-t border-gray-200">
        <d-btn flat @click="$emit('close')">
          Cancel
        </d-btn>
        <d-btn :loading="saving" type="submit" color="primary">
          {{ question ? 'Save Changes' : 'Add Question' }}
        </d-btn>
      </footer>
    </div>
  </form>
</template>

<script>
  import axios from 'axios';
  import { showErrorToast } from '@services/errorHandling.service';
  import { toast } from '@services/toast.service';

  export default {
    emits: ['close', 'saved'],
    
    props: {
      contentListing: {
        type: Object,
        default: () => ({}),
      },
      item: {
        type: Object,
        default: () => ({}),
      },
      question: {
        type: Object,
        default: () => ({}),
      },
    },

    data() {
      return {
        saving: false,
        form: {
          contentListingItemId: this.item.id,
          content: '',
          useTextAnswer: false,
          type: '',
          sortIndex: 0,
          answerChoices: [{
            content: '',
            sortIndex: 0,
            isCorrect: false,
          }],
        },
        emptyChoice: {
          content: '',
          sortIndex: 0,
          isCorrect: false,
        },
      };
    },

    mounted() {
      if (this.question) {
        this.form = JSON.parse(JSON.stringify(this.question));
      }
    },

    methods: {
      addChoice() {
        const copiedChoice = JSON.parse(JSON.stringify(this.emptyChoice));
        this.form.answerChoices.push(copiedChoice);
      },

      saveForm() {
        // Validate the form before saving
        if (!this.validateForm()) {
          return;
        }
        this.createQuestion();
      },

      validateForm() {
        // Basic validation for question content
        if (!this.form.content || this.form.content.trim() === '') {
          toast.error('Please enter a question');
          return false;
        }

        // Validation for multiple choice, dropdown, and multiple answer questions
        if (this.form.type === 'Multiple Choice' || this.form.type === 'Drop Down' || this.form.type === 'Multiple Answer') {
          // Check if there are answer choices
          if (!this.form.answerChoices || this.form.answerChoices.length === 0) {
            toast.error('Please add at least one answer choice');
            return false;
          }

          // Check if at least one answer choice has content
          const validChoices = this.form.answerChoices.filter(choice => choice.content && choice.content.trim() !== '');
          if (validChoices.length === 0) {
            toast.error('Please add at least one answer choice with content');
            return false;
          }

          // Check if at least one correct answer is selected
          const correctAnswers = this.form.answerChoices.filter(choice => choice.isCorrect);
          if (correctAnswers.length === 0) {
            if (this.form.type === 'Multiple Choice' || this.form.type === 'Drop Down') {
              toast.error('Please select the correct answer');
            } else {
              toast.error('Please select at least one correct answer');
            }
            return false;
          }

          // Check if Multiple Answer questions have at least two correct answers
          if (this.form.type === 'Multiple Answer' && correctAnswers.length < 2) {
            toast.error('Please select at least two answers for a multiple selection question. If you only want to have one right answer, use the multiple choice question type.');
            return false;
          }
        }

        return true;
      },

        markAsCorrect(index) {
          if (this.form.type === 'Multiple Answer') {
              this.form.answerChoices[index].isCorrect = !this.form.answerChoices[index].isCorrect;
              return;
          } else {
              this.form.answerChoices.map((m, choiceIndex) => {
                  m.isCorrect = choiceIndex === index;
                  return m;
              });
          }
      },

      async createQuestion() {
        this.saving = true;
        try {
          if (this.question) {
            await axios.patch(`/api/content-listings/items/interactive/questions/${this.question.id}`, this.form);
          } else {
            await axios.post(`/api/content-listings/items/interactive/${this.item.id}/questions`, this.form);
          }
          this.$emit('saved');
        } catch (error) {
          showErrorToast(error);
        } finally {
          this.saving = false;
        }
      },
    },
  };
</script>